import React from 'react';

/**
 * LIFTING A TOOLBAR GLYPH SO IT READS ON THE DARK BAR.
 *
 * <h2>The defect, measured</h2>
 *
 * The study toolbar's drawn icons carry hardcoded Material 800/900 hues — `#4a148c` deep purple,
 * `#0d47a1` blue, `#1b5e20` green, `#00695c` teal — chosen against a white bar. Against the dark
 * theme's `#161b22` they are between **1.1:1 and 3:1**, where 3:1 is the floor for a graphical
 * object being visible at all. Fourteen of the thirty-three distinct colours on that bar were
 * under it, which is a toolbar a reader cannot use rather than one that looks a bit flat.
 *
 * <h2>Lifted, not repainted</h2>
 *
 * The obvious fix is a second palette of thirty-odd hexes, and it is the wrong one twice over:
 * every colour there is semantic — purple is an astro study, green and red are up and down — so a
 * hand-picked dark set is thirty chances to break a meaning, and it is thirty values to keep in
 * step with the light set forever.
 *
 * <p>So the HUE and the SATURATION are kept and only the LIGHTNESS moves, by the least amount
 * that clears the ratio. `#4a148c` becomes a lighter purple rather than a different colour, and
 * an icon that already reads is left exactly alone — which is the half a blanket CSS filter
 * cannot do, since it has no way to be conditional on luminance.
 *
 * <h2>⚠ A GLYPH ON A PLATE IS MEASURED AGAINST THE PLATE</h2>
 *
 * The PTA and PTB buttons are a filled rectangle with a mark on it, and that mark's colour is
 * already chosen by contrast against the plate — see `markOn`. Measured against the BAR it reads
 * 1.11:1 and looks like the worst offender here; lifting it would destroy the one contrast
 * decision on the bar that was actually computed.
 *
 * <p>So a leading full-bleed `<rect>` with an opaque fill becomes the backdrop for everything
 * after it. Without that rule this function would "fix" the two icons that were already right.
 */

/** The minimum contrast a graphical object needs to be seen at all. */
export const MIN_ICON_CONTRAST = 3;

const hexOf = (v) => {
  if (typeof v !== 'string') return null;
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(v.trim());
  if (!m) return null;
  const body = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  return `#${body.toLowerCase()}`;
};

const rgbOf = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  /* eslint-disable no-bitwise */
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  /* eslint-enable no-bitwise */
};

const luminance = (hex) => {
  const c = rgbOf(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};

/** WCAG contrast between two hex colours. */
export function contrast(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const toHsl = (hex) => {
  const [r, g, b] = rgbOf(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h / 6, s, l };
};

const toHex = ({ h, s, l }) => {
  const hue = (p, q, t0) => {
    let t = t0;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r; let g; let b;
  if (s === 0) { r = l; g = l; b = l; } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue(p, q, h + 1 / 3); g = hue(p, q, h); b = hue(p, q, h - 1 / 3);
  }
  return `#${[r, g, b].map((v) => Math.round(v * 255).toString(16).padStart(2, '0')).join('')}`;
};

/**
 * The same colour, light enough to clear `min` against `backdrop`.
 *
 * <p>Returns the input unchanged when it already does — which is most of the bar, and is what
 * makes this a repair rather than a repaint. Lightness is walked up in small steps and the FIRST
 * one that clears is taken, so a colour that was nearly there moves barely at all.
 *
 * <p>It gives up at white rather than looping: a hue that cannot reach the ratio does not exist
 * on this backdrop, but a function that could spin on one would be worse than a pale glyph.
 */
export function liftForContrast(colour, backdrop, min = MIN_ICON_CONTRAST) {
  const hex = hexOf(colour);
  const bg = hexOf(backdrop);
  if (!hex || !bg) return colour;
  if (contrast(hex, bg) >= min) return colour;

  const { h, s } = toHsl(hex);
  for (let l = toHsl(hex).l; l <= 1.0001; l += 0.02) {
    const candidate = toHex({ h, s, l });
    if (contrast(candidate, bg) >= min) return candidate;
  }
  return '#ffffff';
}

/** Does this element paint an opaque full-bleed plate the rest of the glyph sits on? */
const isPlate = (node) => React.isValidElement(node)
  && node.type === 'rect'
  && hexOf(node.props.fill) !== null
  // Full-bleed: a plate is the background of the icon, not a small swatch inside it. The two in
  // this toolbar are 44x32 in a 46x34 viewBox.
  && Number(node.props.width) >= 20 && Number(node.props.height) >= 20;

/**
 * A React element tree with every unreadable `fill` and `stroke` lifted.
 *
 * <p>Non-hex values are left exactly alone: `currentColor` already follows the theme, `none` is
 * not a colour, and an `rgba()` carries an alpha this cannot reason about without knowing what is
 * behind it. Touching any of them would be guessing.
 */
export function legibleIcon(node, backdrop, min = MIN_ICON_CONTRAST) {
  if (!React.isValidElement(node)) return node;

  /*
   * THIS node's own colours are measured against the backdrop it was HANDED. A plate changes the
   * backdrop for its SIBLINGS, never for itself — measuring the plate against its own fill gives
   * a contrast of exactly 1:1 and lifts it to white, which is the first thing this function did
   * and the reason the test below exists. The amber PTA plate came out of it pure white.
   */
  const next = {};
  ['fill', 'stroke'].forEach((attr) => {
    const raw = node.props[attr];
    if (hexOf(raw)) {
      const lifted = liftForContrast(raw, backdrop, min);
      if (lifted !== raw) next[attr] = lifted;
    }
  });

  const kids = React.Children.toArray(node.props.children);
  const plate = kids.find(isPlate);
  // A plate resets the backdrop for everything in the same SVG — see the warning above.
  const inner = plate ? hexOf(plate.props.fill) : backdrop;

  /*
   * Mapped by hand rather than with `React.Children.map`, which always allocates a new array —
   * so the identity check below could never hold and every icon was cloned on every render,
   * defeating React's own bail-out. `changed` is what makes an untouched glyph free.
   */
  let changed = false;
  const mapped = kids.map((child) => {
    // The plate is its own backdrop's sibling, so it is NOT re-measured against itself.
    const out = child === plate ? child : legibleIcon(child, inner, min);
    if (out !== child) changed = true;
    return out;
  });

  if (Object.keys(next).length === 0 && !changed) return node;
  return React.cloneElement(node, next, kids.length > 0 ? mapped : node.props.children);
}

export default legibleIcon;
